import { z } from "zod";
import { PaginationRequestSchema, PaginationResponseSchema } from "./pagination";

// ======================
// POOL PREDICTION QUERY
// ======================
export const PoolPredictionQuerySchema = PaginationRequestSchema.extend({
  poolId: z.string().optional(),
  participantId: z.string().optional(),
  fixtureId: z.string().optional(),
});
export type PoolPredictionQueryDTO = z.infer<typeof PoolPredictionQuerySchema>;

// ======================
// PREDICTION SCORE SUMMARY
// ======================
export const MatchedRuleSchema = z.object({
  type: z.string(),
  label: z.string().optional(),
  points: z.number(),
  category: z.enum(['result', 'bonus']),
});
export type MatchedRuleDTO = z.infer<typeof MatchedRuleSchema>;

export const PredictionScoreSummarySchema = z.object({
  matchedRules: z.array(MatchedRuleSchema),
  calculatedAt: z.string().datetime(),
  isUniqueHit: z.boolean(),
});
export type PredictionScoreSummaryDTO = z.infer<typeof PredictionScoreSummarySchema>;

// ======================
// POOL PREDICTIONS
// ======================
export const PoolPredictionResponseSchema = z.object({
  id: z.string(),
  poolId: z.string(),
  participantId: z.string(),
  username: z.string().nullable().optional(),
  fixtureId: z.string(),
  homeScore: z.number(),
  awayScore: z.number(),
  predictedWinner: z.enum(["HOME", "AWAY", "DRAW"]).nullable().optional(),
  winner: z.enum(["HOME", "AWAY", "DRAW"]).nullable().optional(),
  totalPoints: z.number(),
  scoreSummary: PredictionScoreSummarySchema,
  matchResult: z.object({
    home: z.number(),
    away: z.number(),
    statusId: z.string().nullable(),
  }).nullable().optional(),
  scoredAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type PoolPredictionResponseDTO = z.infer<typeof PoolPredictionResponseSchema>;

export const PoolPredictionsPaginatedResponseSchema = PaginationResponseSchema(
  PoolPredictionResponseSchema,
);
export type PoolPredictionsPaginatedResponseDTO = z.infer<
  typeof PoolPredictionsPaginatedResponseSchema
>;

export const CreatePoolPredictionRequestSchema = z.object({
  poolId: z.string().min(1),
  fixtureId: z.string().min(1),
  homeScore: z.number().int().min(0),
  awayScore: z.number().int().min(0),
  predictedWinner: z.enum(["HOME", "AWAY", "DRAW"]).nullable().optional(),
});
export type CreatePoolPredictionRequestDTO = z.infer<typeof CreatePoolPredictionRequestSchema>;

export const UpdatePoolPredictionRequestSchema = z.object({
  homeScore: z.number().int().min(0).optional(),
  awayScore: z.number().int().min(0).optional(),
  predictedWinner: z.enum(["HOME", "AWAY", "DRAW"]).nullable().optional(),
  homePenalties: z.number().int().min(0).nullable().optional(),
  awayPenalties: z.number().int().min(0).nullable().optional(),
  totalPoints: z.number().int().min(0).optional(),
  scoreSummary: PredictionScoreSummarySchema.optional(),
  scoredAt: z.string().datetime().nullable().optional(),
});
export type UpdatePoolPredictionRequestDTO = z.infer<typeof UpdatePoolPredictionRequestSchema>;
